// ═══════════════════════════════════════════════════════════════════════════
// AI → EditorCommand translator — MATCHPROP domain (CC-2c PR-1)
// ═══════════════════════════════════════════════════════════════════════════
//
// Translates the MATCHPROP AI command shape (MATCH_PROPERTIES) into the internal
// `EditorCommand`s the store dispatches (`matchprop/set-source` +
// `matchprop/apply`). Every path runs the CC-2a fail-closed guard
// (`assertAiCallableMatchpropAction`) BEFORE producing any EditorCommand, so the
// safe contour rejects a non-AI-callable / unknown target without mutating the
// document. Entity properties are only ever changed by the reducer the returned
// commands flow into — never written here — honouring the CLAUDE.md rules:
//   "Properties, CHPROP, MATCHPROP и OSNAP — это системные механизмы." +
//   "AI drafting … может только предлагать и исполнять команды через безопасный
//    контур." + "любые изменения геометрии только через Command Engine."
//
// The thin per-action translator lives in the PACKAGE-INTERNAL sibling
// `matchpropTranslators.ts` (NOT re-exported by the barrel), so the ONLY public
// MATCHPROP entry is the guarded `translateMatchpropCommand` below. This mirrors
// the DRAW-MODIFY domain (`drawModifyDispatcher.ts`) and the VIEW+SPACE domain
// (`viewSpaceDispatcher.ts`), preventing a consumer of
// `@dxf-ai-editor/ai-commands` from emitting a property-paint command past the
// fail-closed guard.

import type { EditorCommand } from "@dxf-ai-editor/contracts";
import {
  ALL_COMMAND_DESCRIPTORS,
  type CommandDescriptor,
} from "@dxf-ai-editor/command-registry";

import {
  normalizeDispatcherArgs,
  type AiDispatchContext,
} from "./blockDispatcherShared";
import { assertAiCallableMatchpropAction } from "./matchpropGuard";
import type { MatchpropAiCommand } from "./matchpropSchemas";
import { translateMatchProperties } from "./matchpropTranslators";

// Re-export the MATCHPROP safe-contour guard (mirrors drawModifyDispatcher
// re-exporting the draw guard) so consumers can reach it from this domain entry
// point — the raw per-action translator stays package-internal.
export {
  MATCHPROP_AI_ACTION_TARGET_COMMAND,
  assertAiCallableMatchpropAction,
} from "./matchpropGuard";

/**
 * Top-level MATCHPROP dispatcher. Runs the CC-2a fail-closed guard, then routes
 * the AI command to the translator that owns its `action`. Returns the flat
 * `EditorCommand[]` the store should dispatch (the caller wraps the batch in one
 * grouped-undo unit — see the app safe contour). `context` is accepted for
 * signature parity with the sibling domain dispatchers (MATCHPROP is
 * self-contained — it references entity ids directly — so it reads no live
 * document state); `descriptors` is injectable for tests / alternate registries.
 */
export function translateMatchpropCommand(
  command: MatchpropAiCommand,
  context: AiDispatchContext = {},
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): EditorCommand[] {
  // Normalise a legacy `(command, descriptorArray)` call so a descriptor array
  // passed as the 2nd arg is treated as `descriptors`, not `context` — keeping
  // the fail-closed guard honoured for restricted registries (shared one-truth
  // `normalizeDispatcherArgs`; mirrors drawModifyDispatcher / viewSpaceDispatcher).
  ({ descriptors } = normalizeDispatcherArgs(context, descriptors));
  // Safe-contour gate (CC-2a): refuse to translate any MATCHPROP action whose
  // target command is not `aiCallable: true`. Fails closed before any
  // EditorCommand is produced. `descriptors` is injectable for tests.
  assertAiCallableMatchpropAction(command.action, descriptors);
  switch (command.action) {
    case "MATCH_PROPERTIES":
      return translateMatchProperties(command);
  }
}
